import React from "react";

import {
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Grid,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { Controller, useFieldArray, useWatch } from "react-hook-form";

import { msgs } from "@config/conf";

import {
  getPluralSuffix,
  validateMaxLength,
  isValidNormalText,
} from "../helpers";

const useStyles = makeStyles((theme) => ({
  groupBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: "50vh",
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  groupBoxError: {
    borderColor: theme.palette.error.main,
    boxShadow: `0 0 0 1px ${theme.palette.error.main} inset`,
  },
}));

const RepeatingGroup = ({ config, control, errors, groupErrors }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: config.name,
  });

  const watchedCategories = useWatch({
    control,
    name: config.name,
  });

  const classes = useStyles();

  const hasGroupError = Array.isArray(groupErrors) && groupErrors.length > 0;

  return (
    <FormControl fullWidth error={hasGroupError}>
      <div
        className={clsx(classes.groupBox, {
          [classes.groupBoxError]: hasGroupError,
        })}
      >
        <Typography variant="h6">{config.label}</Typography>

        {fields.map((item, index) => {
          const entryErrors = errors?.[config.name]?.[index] || {};

          const category = watchedCategories?.[index]?.category;

          const conditionalFields =
            config.fields.find((f) => f.name === "category")
              ?.conditionalFields?.[category] || [];

          return (
            <div key={item.id} className="repeating-entry">
              <Grid
                container
                spacing={2}
                key={index}
                style={{ marginBottom: 20 }}
              >
                {config.fields.map((field) => {
                  const fieldName = `${config.name}[${index}].${field.name}`;
                  const error = entryErrors[field.name];

                  switch (field.type) {
                    case "text":
                    case "number":
                    case "date":
                      return (
                        <Grid item xs={6} key={field.name}>
                          <Controller
                            key={fieldName}
                            name={fieldName}
                            control={control}
                            rules={{
                              required: field.required
                                ? msgs.fieldError
                                : false,
                              validate: (value) => {
                                if (validateMaxLength(value, field.maxLength)) {
                                  return `Maximum length of ${field.maxLength} character${getPluralSuffix(field.maxLength)} exceeded`;
                                }
                                if (
                                  field.validator === "normalText" &&
                                  !isValidNormalText(value)
                                ) {
                                  return msgs.normalTextError;
                                }
                                return true;
                              },
                              pattern:
                                field.validator === "ssn"
                                  ? {
                                      value:
                                        /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
                                      message:
                                        "Invalid SSN format (e.g., 123-45-6789) and must not start with 000, 666, or 900–999",
                                    }
                                  : undefined,
                            }}
                            render={({ field: controllerField }) => (
                              <TextField
                                {...controllerField}
                                label={field.label}
                                type={field.type}
                                fullWidth
                                margin="normal"
                                error={!!error}
                                helperText={error?.message}
                                InputLabelProps={
                                  field.type === "date" ? { shrink: true } : {}
                                }
                                onChange={(e) => {
                                  let value = e.target.value;

                                  if (field.validator === "ssn") {
                                    let rawValue = value.replace(/\D/g, ""); // Remove non-digit characters

                                    let formattedValue = rawValue;
                                    if (
                                      rawValue.length > 3 &&
                                      rawValue.length <= 5
                                    ) {
                                      formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`;
                                    } else if (rawValue.length > 5) {
                                      formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3, 5)}-${rawValue.slice(5, 9)}`;
                                    }

                                    controllerField.onChange(formattedValue);
                                  } else {
                                    controllerField.onChange(value); // Allow normal text input
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>
                      );

                    case "select":
                      return (
                        <Grid item xs={6} key={field.name}>
                          <Controller
                            key={fieldName}
                            name={fieldName}
                            control={control}
                            rules={{
                              required: field.required
                                ? msgs.fieldError
                                : false,
                            }}
                            render={({ field: controllerField }) => (
                              <TextField
                                {...controllerField}
                                select
                                label={field.label}
                                fullWidth
                                margin="normal"
                                error={!!error}
                                helperText={error?.message}
                              >
                                {field.options.map((opt) =>
                                  typeof opt === "string" ? (
                                    <MenuItem key={opt} value={opt}>
                                      {opt}
                                    </MenuItem>
                                  ) : (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  )
                                )}
                              </TextField>
                            )}
                          />
                        </Grid>
                      );

                    case "radio":
                      return (
                        <Grid item xs={6} key={field.name}>
                          <Controller
                            key={fieldName}
                            name={fieldName}
                            control={control}
                            rules={{
                              required: field.required
                                ? msgs.fieldError
                                : false,
                            }}
                            render={({ field: controllerField }) => (
                              <FormControl
                                component="fieldset"
                                error={!!error}
                                style={{
                                  padding: "4px 0",
                                  marginBottom: "8px",
                                  border: "none",
                                }}
                              >
                                <Typography
                                  style={{
                                    color: errors?.[fieldName]
                                      ? "red"
                                      : "inherit",
                                    fontSize: "14px",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {field.label}
                                </Typography>
                                <RadioGroup
                                  {...controllerField}
                                  value={controllerField.value || ""}
                                  row
                                >
                                  {field.options.map((opt) =>
                                    typeof opt === "string" ? (
                                      <FormControlLabel
                                        key={opt}
                                        value={opt}
                                        control={<Radio />}
                                        label={opt}
                                      />
                                    ) : (
                                      <FormControlLabel
                                        key={opt.value}
                                        value={opt.value}
                                        control={<Radio />}
                                        label={opt.label}
                                      />
                                    )
                                  )}
                                </RadioGroup>
                                {error && (
                                  <FormHelperText
                                    style={{
                                      marginTop: "2px",
                                      fontSize: "11px",
                                      lineHeight: "1.2",
                                      color: "red",
                                    }}
                                  >
                                    {error?.message}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                      );

                    default:
                      return null;
                  }
                })}

                {/* Conditional Fields */}
                {conditionalFields.map((field) => {
                  const fieldName = `${config.name}[${index}].${field.name}`;
                  const error = entryErrors[field.name];

                  return (
                    <Grid item xs={6} key={field.name}>
                      <Controller
                        key={fieldName}
                        name={fieldName}
                        control={control}
                        rules={{
                          required: field.required ? msgs.fieldError : false,
                          validate: (value) => {
                            if (validateMaxLength(value, field.maxLength)) {
                              return `Maximum length of ${field.maxLength} character${getPluralSuffix(field.maxLength)} exceeded`;
                            }
                            if (
                              field.validator === "normalText" &&
                              !isValidNormalText(value)
                            ) {
                              return msgs.normalTextError;
                            }
                            return true;
                          },
                          pattern:
                            field.validator === "ssn"
                              ? {
                                  value:
                                    /^(?!000|666|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
                                  message:
                                    "Invalid SSN format (e.g., 123-45-6789) and must not start with 000, 666, or 900–999",
                                }
                              : undefined,
                        }}
                        render={({ field: controllerField }) => (
                          <TextField
                            {...controllerField}
                            label={field.label}
                            type={field.type} // "text" or "date"
                            fullWidth
                            margin="normal"
                            error={!!error}
                            helperText={error?.message}
                            select={field.type === "select"} // Only apply if it's a dropdown
                            value={controllerField.value || ""}
                            InputLabelProps={
                              field.type === "date" ? { shrink: true } : {}
                            }
                            onChange={(e) => {
                              let value = e.target.value;

                              if (field.validator === "ssn") {
                                let rawValue = value.replace(/\D/g, ""); // Remove non-digit characters

                                let formattedValue = rawValue;
                                if (
                                  rawValue.length > 3 &&
                                  rawValue.length <= 5
                                ) {
                                  formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3)}`;
                                } else if (rawValue.length > 5) {
                                  formattedValue = `${rawValue.slice(0, 3)}-${rawValue.slice(3, 5)}-${rawValue.slice(5, 9)}`;
                                }

                                controllerField.onChange(formattedValue);
                              } else {
                                controllerField.onChange(value); // Allow normal text input
                              }
                            }}
                          >
                            {/* Only render options if it's a select field */}
                            {field.type === "select" &&
                              Array.isArray(field.options) &&
                              field.options.map((opt) =>
                                typeof opt === "string" ? (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ) : (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                )
                              )}
                          </TextField>
                        )}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={12}>
                  <Button
                    onClick={() => remove(index)}
                    color="secondary"
                    style={{ marginTop: 10 }}
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            </div>
          );
        })}

        <Button
          onClick={() =>
            append({ beneficiaryType: "", percentage: "", category: "" })
          }
          variant="outlined"
          color="primary"
          style={{ marginTop: 20 }}
        >
          Add {config.label}
        </Button>
      </div>
      <FormHelperText
        component="div"
        style={{
          fontSize: "0.9rem",
          fontWeight: "bold",
        }}
      >
        {hasGroupError && (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {groupErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        )}
      </FormHelperText>
    </FormControl>
  );
};

export default RepeatingGroup;
